import { useState, useEffect } from "react";
import {
  Autocomplete as MuiAutocomplete,
  Box as MuiBox,
  Button as MuiButton,
  CircularProgress as MuiCircularProgress,
  Stack as MuiStack,
  TextField as MuiTextField,
  Tooltip as MuiTooltip,
  Typography as MuiTypography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  LOADER_TYPES,
  UPLOAD_TO_ONCORE_FILTER_TYPES,
} from "app/services/OISService/constants";
import { useOIS } from "app/services/OISService/OISProvider";
import { oncoreEnvsService } from "app/services/oncoreEnvsService";
import UploadOncoreDialog from "./UploadOncoreDialog";

const UploadToOnCore = ({ file, csvType, uploadLink }) => {
  const { t } = useTranslation();
  const [openUploadOnCoreDialog, setOpenUploadOnCoreDialog] = useState(false);
  const {
    isLoading,
    uploadEnvironments: { environments },
    selectedEnvironment,
    setSelectedEnvironment,
    uploadToOnCore,
  } = useOIS();

  const [hasOncoreProduction, setHasOncoreProduction] = useState(false);
  const [hasOncoreTango, setHasOncoreTango] = useState(false);
  const [hasOncoreTest, setHasOncoreTest] = useState(true);
  const [hasOncoreIndigo, setHasOncoreIndigo] = useState(true);
  const [hasOncoreTraining, setHasOncoreTraining] = useState(true);
  const [hasOncoreStaging, setHasOncoreStaging] = useState(true);
  const [hasOncoreCloudProduction, setHasOncoreCloudProduction] =
    useState(true);
  const [hasOncoreShadow, setHasOncoreShadow] = useState(true);

  useEffect(() => {
    setHasOncoreProduction(oncoreEnvsService.hasOncoreProduction());
    setHasOncoreTest(oncoreEnvsService.hasOncoreTest());
    setHasOncoreTango(oncoreEnvsService.hasOncoreTango());
    setHasOncoreIndigo(oncoreEnvsService.hasOncoreIndigo());
    setHasOncoreTraining(oncoreEnvsService.hasOncoreTraining());
    setHasOncoreStaging(oncoreEnvsService.hasOncoreStaging());
    setHasOncoreCloudProduction(oncoreEnvsService.hasOncoreCloudProduction());
    setHasOncoreShadow(oncoreEnvsService.hasOncoreShadow());
  }, []);

  const handleUploadToOnCore = (selected) => {
    const formData = new FormData();
    if (file) {
      formData.append("file", file);
    }

    uploadToOnCore(csvType, selectedEnvironment?.value, uploadLink, formData);

    setOpenUploadOnCoreDialog(false);
  };

  const getPopupMessage = (onCoreEnv) => {
    switch (csvType) {
      case t("csvTypes.eProtocol"):
        return t("uploadToOnCore.dialog.message.eProtocol", {
          environment: onCoreEnv,
        });
      case t("csvTypes.participants"):
        return t("uploadToOnCore.dialog.message.participants", {
          environment: onCoreEnv,
        });
      case t("csvTypes.training"):
        return t("uploadToOnCore.dialog.message.training", {
          environment: onCoreEnv,
        });
      default:
        return "unknown csvType";
    }
  };

  const filteredEnvironments = environments.filter((env) => {
    switch (env.value) {
      case "production":
        return hasOncoreProduction;
      case "tango":
        return hasOncoreTango;
      case "test":
        return hasOncoreTest;
      case "indigo":
        return hasOncoreIndigo;
      case "training":
        return hasOncoreTraining;
      case "staging":
        return hasOncoreStaging;
      case "cloudproduction":
        return hasOncoreCloudProduction;
      case "shadow":
        return hasOncoreShadow;
      default:
        return true;
    }
  });

  return (
    <MuiStack gap={1.75}>
      <MuiTypography variant="h2" sx={{ fontSize: "21px", fontWeight: 100 }}>
        {t("uploadToOnCore.title")}
      </MuiTypography>
      <MuiStack alignItems="center" direction="row" gap={1.75}>
        <MuiAutocomplete
          id="oncore-environment-select"
          name={UPLOAD_TO_ONCORE_FILTER_TYPES.ONCORE_ENVIRONMENT}
          options={filteredEnvironments}
          getOptionLabel={(option) => option.label || ""}
          value={selectedEnvironment}
          onChange={(e, val) => setSelectedEnvironment(val)}
          openOnFocus
          clearOnEscape
          renderInput={(params) => (
            <MuiTextField
              {...params}
              label={t("uploadToOnCore.label.selectOnCoreEnvironment")}
              variant="outlined"
              size="small"
            />
          )}
          renderOption={(props, option) => (
            <div style={{ fontSize: "14px" }} {...props} key={props.key}>
              {option.label}
            </div>
          )}
          sx={{
            width: 250,
            "& .MuiAutocomplete-endAdornment .MuiIconButton-root": {
              // Move dropdown arrow and clear X up to horizontal center
              height: "auto",
            },
            "& .MuiAutocomplete-popupIndicatorOpen": {
              // Stop MUI from rotating the icon by rotating it back
              rotate: "180deg",
            },
            "& .MuiInputLabel-root, .MuiInputBase-input": {
              fontSize: "14.5px",
            },
          }}
        />

        {!isLoading[LOADER_TYPES.UPLOAD_TO_ONCORE] ? (
          <MuiTooltip
            title={t("uploadToOnCore.tooltip.upload")}
            placement="top"
            arrow
          >
            <MuiBox component="span">
              <MuiButton
                variant="contained"
                disabled={!selectedEnvironment}
                onClick={() => setOpenUploadOnCoreDialog(true)}
                sx={{
                  backgroundColor: "#800000",
                }}
              >
                {t("buttons.caps.upload")}
              </MuiButton>
            </MuiBox>
          </MuiTooltip>
        ) : (
          <MuiCircularProgress size="25.7143px" />
        )}
      </MuiStack>

      <UploadOncoreDialog
        isOpen={openUploadOnCoreDialog}
        onClose={() => setOpenUploadOnCoreDialog(false)}
        environment={selectedEnvironment?.label}
        message={getPopupMessage(selectedEnvironment?.label)}
        handleUploadToOnCore={handleUploadToOnCore}
      />
    </MuiStack>
  );
};

export default UploadToOnCore;
